import type { PrimeOMSParams } from '@src/components/OMS/NewOrder/types';
import { OMSView } from '@src/components/OMS/OMSView';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  Button,
  ButtonSelect,
  FormControlSizes,
  JITTooltip,
  MixpanelEvent,
  ProductTypeEnum,
  ReduceOnlyEnum,
  UnifiedLiquidityEnum,
  toBigWithDefault,
  useDynamicCallback,
  useMixpanel,
  type AutocompleteGroupSorterFunc,
  type ButtonSelectProps,
  type Leaves,
  type Security,
} from '@talos/kyoko';
import { capitalize, get } from 'lodash-es';
import { useMemo, useState } from 'react';
import type { AppState } from '../../providers/AppStateProvider/types';
import type { OMSFormOrderParameters } from '../../providers/OMSContext.types';
import type { TradePositionButtonAction } from '../../utils/getPositionSecurities';
import { useGetTradePositionDetails, type GetTradePositionDetailsParams } from '../../utils/getTradePositionDetails';
import { primeNewOrderForm } from '../OMS/NewOrder/OrderSlice';
import { primeNewRFQForm } from '../OMS/NewRFQ/RFQSlice';
import { isFalconXOption } from '../OMS/NewRFQ/utils';
import { openView } from '../OMS/OMSSlice';
import { useGetReduceOnlySupport } from '../OMS/useGetReduceOnlySupport';
import { getPrimeOrderFormInstructions } from './getPrimeOrderFormInstructions';
import { useCommonTradeSpotPositionCurrencies } from './useCommonTradeSpotPositionCurrencies';

export type TradePositionButtonProps<T> = {
  /** Define what asset type all entities are, or a function defining how to resolve the asset type per entity. */
  getAssetType: ProductTypeEnum | ((item: T) => ProductTypeEnum);
  /** If you want to be closing positions using sub accounts present on the entity, you need to provide a SubAccount field so the column can resolve it. */
  subAccountField?: Leaves<T>;
  /** The action the button rendered in the column should represent -- either a buy, sell or a "close" (buy or sell depending on Amount) */
  action: TradePositionButtonAction;
  /** The width of the containing buttons */
  buttonWidth?: number;
} & Pick<
  GetTradePositionDetailsParams<T>,
  'position' | 'assetField' | 'constrainSecuritiesToMarket' | 'marketAccountField' | 'getAmount'
>;

export function TradePositionButton<T>({
  position,
  action,
  getAmount,
  getAssetType,
  assetField,
  constrainSecuritiesToMarket,
  marketAccountField,
  subAccountField,
  buttonWidth,
}: TradePositionButtonProps<T>) {
  const mixpanel = useMixpanel();
  const assetType = resolveAssetType(position, getAssetType);

  const { getReduceOnlySupport } = useGetReduceOnlySupport();
  const strategyField = useAppStateSelector((state: AppState) => state.order.form.strategyField);
  const unifiedLiquidity = useAppStateSelector((state: AppState) => state.order.form.parameters?.UnifiedLiquidity);
  const isUnifiedLiquidityEnabled = unifiedLiquidity?.value === UnifiedLiquidityEnum.Enabled;
  const getTradePositionDetails = useGetTradePositionDetails();

  const asset: string | undefined = useMemo(() => {
    const resolved = position && assetField ? get(position, assetField) : undefined;
    return typeof resolved === 'string' ? resolved : undefined;
  }, [position, assetField]);

  const dispatch = useAppStateDispatch();
  const handlePrimeTradePosition = useDynamicCallback(
    ({ position, securityToTradeThrough }: { position: T; securityToTradeThrough: Security }) => {
      if (!securityToTradeThrough || !position || !asset) {
        return; // just for safety...
      }

      const maybeAccount = marketAccountField ? get(position, marketAccountField) : undefined;
      const marketAccounts = typeof maybeAccount === 'string' ? [maybeAccount] : [];

      const { currency, side, amount } = getPrimeOrderFormInstructions({
        asset,
        positionAmount: getAmount(position),
        securityToTradeThrough,
        action,
      });

      const amountBig = toBigWithDefault(amount, 0);
      // Only prime with an amount if our amount is non-zero. Priming with 0 never makes sense.
      const nonZeroAmount = amountBig.eq(0) ? undefined : amountBig.abs().toFixed();

      const maybeSubAccount = subAccountField ? get(position, subAccountField) : undefined;

      if (shouldCloseViaRFQ(securityToTradeThrough)) {
        dispatch(
          primeNewRFQForm({
            symbol: securityToTradeThrough.Symbol,
            side,
            orderQty: nonZeroAmount,
            marketAccounts,
            subAccount: typeof maybeSubAccount === 'string' ? maybeSubAccount : undefined,
            currency,
          })
        );
        dispatch(openView(OMSView.RFQForm));
      } else {
        // default case, close via order form
        const parameters: OMSFormOrderParameters = {
          reduceOnly: getReduceOnlySupport(
            securityToTradeThrough,
            strategyField.value,
            marketAccounts,
            isUnifiedLiquidityEnabled
          )
            ? ReduceOnlyEnum.Enabled
            : ReduceOnlyEnum.Disabled,
        };

        const primeOmsParams: PrimeOMSParams = {
          symbol: securityToTradeThrough.Symbol,
          side,
          orderQty: nonZeroAmount,
          marketAccounts,
          subAccount: typeof maybeSubAccount === 'string' ? maybeSubAccount : undefined,
          currency,
          parameters,
        };

        dispatch(primeNewOrderForm(primeOmsParams));
        dispatch(openView(OMSView.NewOrderForm));
      }
    }
  );

  const handleSelection = useDynamicCallback((security: Security) => {
    if (!position) {
      return;
    }
    mixpanel.track(MixpanelEvent.ClosePosition);
    handlePrimeTradePosition({ position, securityToTradeThrough: security });
  });

  const { notTradableReason, tradeButtonVisible, tradable, possibleSecurities } = useMemo(
    () =>
      getTradePositionDetails({
        position,
        getAmount,
        assetField: assetField,
        constrainSecuritiesToMarket,
        marketAccountField,
        subAccountField,
        assetType,
        action,
      }),
    [
      getTradePositionDetails,
      getAmount,
      position,
      assetField,
      constrainSecuritiesToMarket,
      marketAccountField,
      assetType,
      action,
      subAccountField,
    ]
  );

  if (!tradeButtonVisible || !asset) {
    return <></>;
  }

  return assetType === ProductTypeEnum.Spot ? (
    <TradeButtonForSpot
      possibleSecurities={possibleSecurities}
      onSelection={handleSelection}
      action={action}
      notTradableReason={notTradableReason}
      asset={asset}
      dataTestID="close-position-button"
      buttonWidth={buttonWidth}
    />
  ) : (
    <JITTooltip tooltip={notTradableReason}>
      <Button
        onClick={() => handleSelection(possibleSecurities[0])}
        size={FormControlSizes.Small}
        disabled={!tradable}
        data-testid="close-position-button"
        style={{
          width: buttonWidth,
        }}
      >
        {capitalize(action)}
      </Button>
    </JITTooltip>
  );
}

/** We don't have any generic mechanism to check for knowing if we should trade via RFQ or Order... for now, we are hardcoding
 * to if it is a FalconX option. FalconX options can only be RFQed
 */
function shouldCloseViaRFQ(security: Security) {
  return isFalconXOption(security);
}

function resolveAssetType<T>(entity: T | undefined, getAssetType: TradePositionButtonProps<T>['getAssetType']) {
  if (typeof getAssetType === 'function') {
    return entity ? getAssetType(entity) : undefined;
  }

  return getAssetType;
}

interface TradeButtonForSpotProps {
  possibleSecurities: Security[];
  onSelection: (security: Security) => void;
  action: TradePositionButtonProps<any>['action'];
  notTradableReason: string | undefined;
  asset: string;
  dataTestID?: string;
  buttonWidth?: number;
}

const autoCompleteGroupSorterFunc: AutocompleteGroupSorterFunc = (a, b) => {
  // We have 2 groups, common and all, sort common to the top.
  return a.group === 'Common' ? -1 : 1;
};

// We render different buttons based on if we're gonna be closing a spot or derivative position.
function TradeButtonForSpot({
  possibleSecurities,
  onSelection,
  action,
  notTradableReason,
  asset,
  dataTestID,
  buttonWidth,
}: TradeButtonForSpotProps) {
  const [renderSelector, setRenderSelector] = useState(false);
  const commonCurrencies = useCommonTradeSpotPositionCurrencies();

  const commonCurrenciesSet = useMemo(
    // If the asset we're trying to perform an action on is itself in the common set, then we create a new common subset
    // without "ourselves", otherwise every spot trading pair would be regarding as "common"
    () => new Set(commonCurrencies.filter(c => c !== asset)),
    [commonCurrencies, asset]
  );

  const searchSelectProps: ButtonSelectProps<Security>['useSearchSelectProps'] = useMemo(
    () => ({
      items: possibleSecurities,
      getLabel: security => security.DisplaySymbol,
      getGroup: security => {
        const isCommon =
          (security.BaseCurrency && commonCurrenciesSet.has(security.BaseCurrency)) ||
          commonCurrenciesSet.has(security.QuoteCurrency);
        return isCommon ? 'Common' : 'All';
      },
      groupSorter: autoCompleteGroupSorterFunc,
      initialIsOpen: true, // this needs to be true since we only render the selector after a click has happened
    }),
    [possibleSecurities, commonCurrenciesSet]
  );

  const dropdownProps: ButtonSelectProps<Security>['useDropdownPopperProps'] = useMemo(
    () => ({
      dropdownWidth: '200px',
      dropdownPlacement: 'bottom',
    }),
    []
  );

  // All this conditional logic below here is for performance reasons.
  // We only want to render the ButtonSelect, which in turn would initialize all the downshift and dropdown-popper stuff, when we click to open the selector.
  // Similarly for the tooltip, we only need to render the tooltip if there's something we want to show in it, and if the user is actually hovering over the content (JustInTimeTooltip)

  const button = useMemo(() => {
    return renderSelector ? (
      <ButtonSelect
        size={FormControlSizes.Small}
        useSearchSelectProps={searchSelectProps}
        useDropdownPopperProps={dropdownProps}
        onSelection={onSelection}
        disabled={notTradableReason != null}
        onClose={() => setRenderSelector(false)}
        dropdownHeight={280}
        dataTestID={dataTestID}
      >
        {capitalize(action)}
      </ButtonSelect>
    ) : (
      <Button
        size={FormControlSizes.Small}
        onClick={() => setRenderSelector(true)}
        disabled={notTradableReason != null}
        data-testid={dataTestID}
        style={{
          width: buttonWidth,
        }}
      >
        {capitalize(action)}
      </Button>
    );
  }, [
    renderSelector,
    searchSelectProps,
    dropdownProps,
    onSelection,
    notTradableReason,
    dataTestID,
    action,
    buttonWidth,
  ]);

  // Only render the tooltip if we're disabled, on top of it also being a JustInTime tooltip.
  return notTradableReason ? <JITTooltip tooltip={notTradableReason}>{button}</JITTooltip> : button;
}
